<template>
  <div class="usuarios-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="usuarios-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-header-left">
          <div class="page-icon-box">
            <v-icon color="#F2F2ED" size="20">mdi-account-multiple</v-icon>
          </div>
          <div>
            <h1 class="page-title">Gestión de usuarios</h1>
            <p class="page-sub">Administra los usuarios del sistema</p>
          </div>
        </div>

        <button class="btn-primary" @click="$router.push('/nuevo-usuario')">
          <v-icon size="15" class="mr-2">mdi-plus</v-icon>
          Nuevo usuario
        </button>
      </header>

      <!-- Tabla -->
      <section class="fade-in delay-200">
        <div class="card-flat card-flat-nopad">
          <div class="table-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Lista de usuarios
            </span>
          </div>

          <v-data-table
            :loading="cargando"
            :headers="encabezadoTabla"
            :items="usuarios"
            sort-by="nombre"
            class="dash-table"
            :footer-props="{ itemsPerPageText: 'Por página' }"
          >
            <template v-slot:[`item.usuario`]="{ item }">
              <div class="flex items-center gap-md">
                <div class="user-avatar">
                  <v-icon size="14" color="#0E0E10">mdi-account</v-icon>
                </div>
                <span class="cell-strong">{{ item.usuario }}</span>
              </div>
            </template>

            <template v-slot:[`item.nombre`]="{ item }">
              <span class="cell-primary">{{ item.nombre }}</span>
            </template>

            <template v-slot:[`item.telefono`]="{ item }">
              <div class="flex items-center gap-xs cell-secondary">
                <v-icon size="12" color="#5C5C62">mdi-phone</v-icon>
                <span>{{ item.telefono || 'Sin teléfono' }}</span>
              </div>
            </template>

            <template v-slot:[`item.opciones`]="{ item }">
              <div class="flex gap-xs">
                <button class="icon-btn" @click="editar(item.id)" title="Editar">
                  <v-icon size="15" color="#8C8C93">mdi-pencil</v-icon>
                </button>
                <button class="icon-btn icon-btn-danger" @click="eliminar(item)" title="Eliminar">
                  <v-icon size="15" color="#FF6B6B">mdi-delete</v-icon>
                </button>
              </div>
            </template>

            <template slot="no-data">
              <div class="empty-state">
                <div class="empty-icon">
                  <v-icon size="28" color="#5C5C62">mdi-account-off</v-icon>
                </div>
                <h3 class="empty-title">No hay usuarios registrados</h3>
                <p class="empty-desc">Comienza agregando el primer usuario del sistema</p>
                <button class="btn-primary mt-lg" @click="$router.push('/nuevo-usuario')">
                  <v-icon size="15" class="mr-2">mdi-plus</v-icon>
                  Crear primer usuario
                </button>
              </div>
            </template>

            <template v-slot:loading>
              <div class="loading-state">
                <div class="spinner"></div>
                <p class="loading-text">Cargando usuarios…</p>
              </div>
            </template>
          </v-data-table>
        </div>
      </section>
    </div>

    <!-- FAB -->
    <button class="fab-button" @click="$router.push('/nuevo-usuario')">
      <v-icon size="22" color="#0E0E10">mdi-plus</v-icon>
    </button>

    <!-- Dialog: Confirmar Eliminación -->
    <v-dialog v-model="mostrarDialogoEliminar" max-width="480px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Confirmar eliminación</h3>
        </div>

        <div class="modal-body">
          <p class="modal-text">
            ¿Estás seguro de eliminar al usuario
            <strong class="text-error">"{{ itemSeleccionado }}"</strong>?
          </p>
          <div class="alert-box">
            <v-icon size="18" color="#FFB84D">mdi-alert</v-icon>
            <div class="alert-text">
              Esta acción no se puede deshacer. El usuario perderá acceso al sistema.
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="cerrarDialogoEliminar">Cancelar</button>
          <button class="btn-danger-solid" @click="confirmarEliminar" :disabled="cargando">
            <span v-if="!cargando">Eliminar</span>
            <span v-else class="loading-inline"><span class="spinner-sm"></span>Eliminando</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="mostrarMensaje" :timeout="3000" :color="mensaje.color" top right>
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <span>{{ mensaje.texto }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import DialogoEliminar from '../Dialogos/DialogoEliminar.vue'
import HttpService from '../../Servicios/HttpService'

export default {
  name: "Usuarios",
  components: { DialogoEliminar },

  data: () => ({
    cargando: false,
    mostrarDialogoEliminar: false,
    usuario: {},
    usuarios: [],
    encabezadoTabla: [
      { text: "Usuario", sortable: true, value: "usuario" },
      { text: "Nombre", sortable: true, value: "nombre" },
      { text: "Teléfono", value: "telefono" },
      { text: 'Opciones', value: 'opciones', sortable: false },
    ],
    itemSeleccionado: "",
    mensaje: {
      texto: "",
      color: ""
    },
    mostrarMensaje: false
  }),

  computed: {
    usuariosActivos() {
      return this.usuarios.filter(u => u.activo !== false).length
    },

    administradores() {
      return this.usuarios.filter(u => u.rol === 'admin' || u.tipo === 'admin').length
    },

    mensajeIcon() {
      switch (this.mensaje.color) {
        case 'success': return 'mdi-check-circle'
        case 'error': return 'mdi-alert-circle'
        case 'warning': return 'mdi-alert'
        default: return 'mdi-information'
      }
    }
  },

  mounted() {
    this.obtenerUsuarios()
  },

  methods: {
    editar(idUsuario) {
      this.$router.push({
        name: "EditarUsuario",
        params: { id: idUsuario },
      })
    },

    eliminar(usuario) {
      this.itemSeleccionado = usuario.nombre
      this.usuario = usuario
      this.mostrarDialogoEliminar = true
    },

    cerrarDialogoEliminar() {
      this.mostrarDialogoEliminar = false
      this.usuario = {}
    },

    async confirmarEliminar() {
      this.cargando = true
      let payload = {
        metodo: "delete",
        id: this.usuario.id
      }

      try {
        const eliminado = await HttpService.eliminar("usuarios.php", payload)

        if (eliminado) {
          this.mostrarDialogoEliminar = false
          this.mostrarMensaje = true
          this.mensaje = {
            texto: "✅ Usuario eliminado correctamente",
            color: "success"
          }
          await this.obtenerUsuarios()
        }
      } catch (error) {
        console.error('Error eliminando usuario:', error)
        this.mostrarMensaje = true
        this.mensaje = {
          texto: "❌ Error al eliminar el usuario",
          color: "error"
        }
      } finally {
        this.cargando = false
      }
    },

    async obtenerUsuarios() {
      this.cargando = true
      let payload = { metodo: "get" }

      try {
        const respuesta = await HttpService.obtenerConDatos(payload, "usuarios.php")
        this.usuarios = respuesta
      } catch (error) {
        console.error('Error cargando usuarios:', error)
        this.mostrarMensaje = true
        this.mensaje = {
          texto: "❌ Error al cargar los usuarios",
          color: "error"
        }
        this.usuarios = []
      } finally {
        this.cargando = false
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.usuarios-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #FFB84D;
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.usuarios-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.usuarios-shell {
  position: relative;
  z-index: 1;
  max-width: 1120px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 24px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

/* ===== CARD / TABLA ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.card-flat-nopad {
  overflow: hidden;
}

.table-head {
  padding: 18px 22px;
  border-bottom: 1px solid var(--card-border);
}

.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.tick-mark {
  width: 12px;
  height: 1px;
  background: var(--accent);
}

.user-avatar {
  width: 28px;
  height: 28px;
  background: var(--accent);
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cell-strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.cell-primary {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
}

.cell-secondary {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.icon-btn {
  width: 28px;
  height: 28px;
  border-radius: var(--radius);
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn:hover {
  border-color: var(--card-border);
  background: rgba(255, 255, 255, 0.03);
}

.icon-btn-danger:hover {
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.06);
}

/* ===== EMPTY / LOADING ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 56px 20px;
}

.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
}

.empty-desc {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 0;
  gap: 16px;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover {
  filter: brightness(1.08);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-primary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.btn-danger-solid {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--error);
  color: #f70c0c;
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-danger-solid:hover:not(:disabled) {
  filter: brightness(1.1);
}

.btn-danger-solid:disabled {
  opacity: 0.6;
  cursor: default;
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.spinner-sm {
  width: 11px;
  height: 11px;
  border: 2px solid rgba(0, 0, 0, 0.25);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== FAB ===== */
.fab-button {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--accent);
  border: none;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.fab-button:hover {
  transform: scale(1.06);
  filter: brightness(1.05);
}

/* ===== MODAL ===== */
.modal-panel {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.modal-head {
  padding: 22px 24px;
  border-bottom: 1px solid var(--card-border);
}

.modal-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.modal-text {
  font-size: 13.5px;
  color: var(--text-secondary);
  margin: 0 0 18px;
  line-height: 1.6;
}

.modal-text strong {
  color: var(--error);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
}

.alert-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(255, 184, 77, 0.08);
  border: 1px solid rgba(255, 184, 77, 0.25);
  border-radius: var(--radius);
  padding: 12px 14px;
}

.alert-text {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-200 { animation-delay: 0.08s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in, .spinner, .spinner-sm { animation: none; }
}

/* ===== Overrides v-data-table (Vuetify) ===== */
.dash-table {
  background: transparent !important;
}

.dash-table >>> .v-data-table__wrapper {
  background: transparent;
}

.dash-table >>> table {
  background: transparent;
}

.dash-table >>> thead th {
  background: rgba(255, 255, 255, 0.02) !important;
  color: var(--text-tertiary) !important;
  text-transform: uppercase;
  font-size: 10px !important;
  letter-spacing: 0.08em;
  font-family: var(--font-mono);
  border-bottom: 1px solid var(--card-border) !important;
  padding: 0 22px !important;
}

.dash-table >>> thead th.sortable .v-data-table-header__icon {
  color: var(--text-tertiary) !important;
}

.dash-table >>> tbody td {
  border-bottom: 1px solid var(--card-border) !important;
  padding: 12px 22px !important;
}

.dash-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.02) !important;
}

.dash-table >>> .v-data-footer {
  color: var(--text-tertiary) !important;
  border-top: 1px solid var(--card-border) !important;
  font-size: 12px;
  padding: 0 22px;
}

.dash-table >>> .v-data-footer__select .v-select__selection {
  color: var(--text-secondary) !important;
}

.dash-table >>> .v-data-footer__icons-before .v-icon,
.dash-table >>> .v-data-footer__icons-after .v-icon {
  color: var(--text-secondary) !important;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .usuarios-page {
    padding: 20px;
  }

  .page-header {
    padding: 18px;
  }

  .dash-table >>> thead th,
  .dash-table >>> tbody td {
    padding: 10px 14px !important;
    font-size: 12px !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Usuarios/Usuarios.vue